import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon.tsx';
import { useBlobAccessUrls } from '@/hooks/useBlobAccessUrl';
import { trackEvent } from '@/utils/fullstory';

import './onboarding.css';

import { readDevOnboardingStep } from './devOnboardingStep';
import { useOnboardingTransition } from './onboardingTransitionContext';
import { saveMarketingBudget } from './saveMarketingBudget';
import { AnalysisScreen } from './screens/AnalysisScreen';
import { BackgroundIntroScreen } from './screens/BackgroundIntroScreen';
import { BudgetScreen } from './screens/BudgetScreen';
import { FirstJobIntroScreen } from './screens/FirstJobIntroScreen';
import { InputScreen } from './screens/InputScreen';
import { IntroScreen } from './screens/IntroScreen';
import { JobPreviewScreen } from './screens/JobPreviewScreen';
import { JobStartedScreen } from './screens/JobStartedScreen';
import { MemoryImportPasteScreen } from './screens/memoryImport/MemoryImportPasteScreen';
import { MemoryImportSelectScreen } from './screens/memoryImport/MemoryImportSelectScreen';
import type { Provider } from './screens/memoryImport/providers';
import { OnlinePresenceScreen } from './screens/OnlinePresenceScreen';
import { PhoneScreen } from './screens/PhoneScreen';
import { PostAnalysisScreen } from './screens/PostAnalysisScreen';
import { ReplyPromptScreen } from './screens/ReplyPromptScreen';
import { StrategyIntroScreen } from './screens/StrategyIntroScreen';
import { StrategyPickScreen } from './screens/StrategyPickScreen';
import type { SlotId, StrategyGoal } from './screens/strategySummary';
import { StrategySummaryScreen } from './screens/StrategySummaryScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';

const SCREENS = [
	'welcome',
	'intro',
	'input',
	'memory_import_select',
	'memory_import_paste',
	'phone',
	'reply_prompt',
	'launching',
	'analysis',
	'post_analysis',
	'strategy_intro',
	'background_intro',
	'first_job_intro',
	'job_preview',
	'job_started',
	'strategy_pick',
	'budget',
	'strategy_summary',
] as const;

type Screen = (typeof SCREENS)[number];

function isScreen(value: string): value is Screen {
	return (SCREENS as readonly string[]).includes(value);
}

/**
 * Derive the UI screen from bridge onboarding phase.
 * Uses available data to skip past completed steps on page refresh.
 */
function phaseToScreen(phase: string | undefined): Screen {
	switch (phase) {
		case 'building': // Falls through — transient phase from request_start_onboarding
		case 'phone':
			return 'phone';
		case 'awaiting_reply':
			return 'reply_prompt';
		case 'launching':
			return 'launching';
		case 'analysis_website':
		case 'analysis_ig':
			return 'analysis';
		case 'results':
			return 'post_analysis';
		default:
			return 'welcome';
	}
}

/**
 * Where the back control lands from each screen. A static map rather than a
 * history stack: after a page refresh the wizard re-derives its screen from
 * the bridge phase with no navigation history, and back must still work.
 *
 * Two screens have no entry, so they render no back control:
 *  - `welcome` is the first screen — nothing to go back to.
 *  - `memory_import_select` is the first screen *after* the input form is
 *    submitted, and that submit fires `request_start_onboarding` — the
 *    one-shot that kicks off every onboarding job. Backing into the input
 *    form would let the user resubmit: the bridge dedups it via
 *    `isOnboardingAlreadyStarted`, so any edited URL/handle is silently
 *    dropped, and but for that guard a second onboarding graph would spin up.
 *    So the input form is a point of no return: no screen backs into it.
 *
 * The processing screens (`launching`, `analysis`) are never back *targets*:
 * backing out of them and out of `post_analysis` lands on `reply_prompt`, so
 * back never forces a replay of the auto-advancing analysis animation.
 */
const BACK_TARGET: Partial<Record<Screen, Screen>> = {
	intro: 'welcome',
	input: 'intro',
	memory_import_paste: 'memory_import_select',
	// Lets users who skipped the memory import go back and run it after all.
	phone: 'memory_import_select',
	reply_prompt: 'phone',
	launching: 'reply_prompt',
	analysis: 'reply_prompt',
	post_analysis: 'reply_prompt',
	strategy_intro: 'post_analysis',
	background_intro: 'strategy_intro',
	first_job_intro: 'background_intro',
	job_preview: 'first_job_intro',
	job_started: 'job_preview',
	strategy_pick: 'job_started',
	budget: 'strategy_pick',
	strategy_summary: 'budget',
};

/**
 * Bridge phase to persist when backing onto a screen, so refresh resumes
 * where the user backed to. Only back targets with their own phase get an
 * entry; everywhere else the phase stays wherever forward navigation put it.
 *
 * Never put `'idle'` here: a phase of `'idle'` reopens
 * `request_start_onboarding`'s already-started gate, and a resubmit in the
 * window before the backend anchors a thread would spin up a second
 * onboarding graph and wipe `onboarding_status`. `'building'` is also wrong —
 * it's a transient phase owned by the start flow and resumes at the phone
 * screen. The narrow value type keeps both out at compile time.
 */
const BACK_PHASE: Partial<Record<Screen, 'phone' | 'awaiting_reply'>> = {
	phone: 'phone',
	reply_prompt: 'awaiting_reply',
};

export function OnboardingWizard() {
	const bridgeApi = useBridgeApi();
	// The budget outlives onboarding: it's written to the Business Profile over
	// REST (the wizard otherwise only talks to the bridge WebSocket) so the
	// user can edit it later in the Brand space.
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const { completeOnboarding } = useOnboardingTransition();

	const onboardingPhase = bridgeApi.user_state?.onboarding_status?.phase;
	const screenshotUrl =
		bridgeApi.user_state?.onboarding_status?.website_screenshot_url;
	const igThumbnails = bridgeApi.user_state?.onboarding_status?.ig_thumbnails;
	const websiteUrl = bridgeApi.user_state?.onboarding_status?.website_url;
	const instagramHandle =
		bridgeApi.user_state?.onboarding_status?.instagram_handle;
	const screenshotResolved =
		bridgeApi.user_state?.onboarding_status?.website_screenshot_resolved;
	const igDataResolved =
		bridgeApi.user_state?.onboarding_status?.ig_data_resolved;
	const [monthlyBudgetUsd, setMonthlyBudgetUsd] = useState(0);
	const [monthlyGoal, setMonthlyGoal] = useState<StrategyGoal | null>(null);
	const [memoryProvider, setMemoryProvider] = useState<Provider | null>(null);
	// Screen transitions remount the active screen (key={screen} below), so
	// values the user already entered live here and re-seed the screens via
	// initial-value props when back navigation returns to them.
	const [phoneNumber, setPhoneNumber] = useState('');
	const [memorySnippet, setMemorySnippet] = useState('');

	// Memory-import report lands minutes after the fire-and-forget
	// request_import_ai_memory dispatch, on whatever screen the user is on —
	// so the notice lives at the overlay level, not on the import screens.
	const memoryImport = bridgeApi.user_state?.onboarding_status?.memory_import;
	// Track the dismissed report by reference identity: each bridge update
	// produces a fresh memory_import object, so a new report (even with the
	// same fields) is a different reference and re-shows the notice.
	const [dismissedMemoryImport, setDismissedMemoryImport] = useState<
		typeof memoryImport | null
	>(null);
	const showMemoryImportNotice =
		!!memoryImport &&
		dismissedMemoryImport !== memoryImport &&
		(memoryImport.status === 'failed' || memoryImport.status === 'partial');

	// Derive initial screen from bridge phase (handles page refresh). In dev, a
	// `?onboardingStep=<screen>` URL param wins, so a specific step can be opened
	// directly for visual QA + e2e — inert in production builds.
	const initialScreen = useMemo(() => {
		const devStep = readDevOnboardingStep();
		if (devStep && isScreen(devStep)) return devStep;
		return phaseToScreen(onboardingPhase);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const [screen, setScreen] = useState<Screen>(initialScreen);

	// The stored thumbnail URLs carry a SAS that lasts 30 minutes, and so does
	// a fresh one. Sign them when the analysis screen opens, so an owner who
	// comes back later, or waits on an earlier screen, gets live ones. The gates
	// below read the stored list.
	const freshIgThumbnails = useBlobAccessUrls(
		screen === 'analysis' ? (igThumbnails ?? undefined) : undefined,
	);

	// Readiness timeout: don't block the launching screen forever if the
	// screenshot or IG thumbnail events never arrive.
	const [readyTimeout, setReadyTimeout] = useState(false);

	useEffect(() => {
		if (!memoryImport) {
			setDismissedMemoryImport(null);
		}
	}, [memoryImport]);

	useEffect(() => {
		if (screen !== 'launching') {
			setReadyTimeout(false);
			return;
		}
		const t = setTimeout(() => setReadyTimeout(true), 180_000);
		return () => clearTimeout(t);
	}, [screen]);

	// ---- Screen transition handlers ----

	const handleBack = useCallback(() => {
		const target = BACK_TARGET[screen];
		if (!target) return;
		trackEvent('Onboarding Step', {
			step: target,
			previous_step: screen,
			went_back: true,
		});
		const phase = BACK_PHASE[target];
		if (phase) {
			bridgeApi.sendAction({ type: 'do_set_onboarding_step', phase });
		}
		setScreen(target);
	}, [bridgeApi, screen]);

	const handleWelcomeDone = useCallback(() => {
		trackEvent('Onboarding Step', { step: 'intro', previous_step: 'welcome' });
		setScreen('intro');
	}, []);

	const handleIntroDone = useCallback(() => {
		trackEvent('Onboarding Step', { step: 'input', previous_step: 'intro' });
		setScreen('input');
	}, []);

	const handleInputSubmit = useCallback(
		(data: { websiteUrl: string; instagramHandle: string }) => {
			let normalizedWebsite = data.websiteUrl.trim() || null;
			if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
				normalizedWebsite = `https://${normalizedWebsite}`;
			}
			const normalizedInstagram =
				data.instagramHandle.trim().replace(/^@/, '') || null;

			trackEvent('Onboarding Started', {
				website_url: normalizedWebsite ?? '',
				instagram_handle: normalizedInstagram ?? '',
			});
			bridgeApi.sendAction({
				type: 'request_start_onboarding',
				website_url: normalizedWebsite,
				instagram_handle: normalizedInstagram,
			});
			bridgeApi.sendAction({
				type: 'do_set_onboarding_step',
				phase: 'phone',
			});
			setScreen('memory_import_select');
		},
		[bridgeApi],
	);

	const handleMemorySelectPick = useCallback((provider: Provider) => {
		trackEvent('Onboarding Step', {
			step: 'memory_import_paste',
			previous_step: 'memory_import_select',
			provider,
		});
		setMemoryProvider(provider);
		setScreen('memory_import_paste');
	}, []);

	const handleMemorySelectSkip = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'phone',
			previous_step: 'memory_import_select',
			skipped_memory_import: true,
		});
		setScreen('phone');
	}, []);

	const handleMemoryPasteSubmit = useCallback(
		(snippet: string) => {
			trackEvent('Onboarding Step', {
				step: 'phone',
				previous_step: 'memory_import_paste',
			});
			setMemorySnippet(snippet);
			bridgeApi.sendAction({ type: 'request_import_ai_memory', snippet });
			setScreen('phone');
		},
		[bridgeApi],
	);

	const handleMemoryPasteSkip = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'phone',
			previous_step: 'memory_import_paste',
			skipped_memory_import: true,
		});
		setScreen('phone');
	}, []);

	const handlePhoneSubmit = useCallback(
		(phone: string) => {
			trackEvent('Onboarding Phone Submitted');
			setPhoneNumber(phone);
			bridgeApi.sendAction({ type: 'do_save_phone_number', phone });
			bridgeApi.sendAction({
				type: 'do_set_onboarding_step',
				phase: 'awaiting_reply',
			});
			setScreen('reply_prompt');
		},
		[bridgeApi],
	);

	const handleReplyConfirmed = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'launching',
			previous_step: 'reply_prompt',
		});
		bridgeApi.sendAction({
			type: 'do_set_onboarding_step',
			phase: 'launching',
		});
		setScreen('launching');
	}, [bridgeApi]);

	const handleReplyBackToPhone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'phone',
			previous_step: 'reply_prompt',
			didnt_get_text: true,
		});
		bridgeApi.sendAction({
			type: 'do_set_onboarding_step',
			phase: 'phone',
		});
		setScreen('phone');
	}, [bridgeApi]);

	const handleLaunchingDone = useCallback(() => {
		const hasWebsiteAsset = !!screenshotUrl;
		const hasIgAsset = Array.isArray(igThumbnails) && igThumbnails.length > 0;
		if (!hasWebsiteAsset && !hasIgAsset) {
			trackEvent('Onboarding Step', {
				step: 'post_analysis',
				previous_step: 'launching',
				skipped_analysis: true,
			});
			bridgeApi.sendAction({
				type: 'do_set_onboarding_step',
				phase: 'results',
			});
			setScreen('post_analysis');
			return;
		}
		trackEvent('Onboarding Step', {
			step: 'analysis',
			previous_step: 'launching',
		});
		bridgeApi.sendAction({
			type: 'do_set_onboarding_step',
			phase: 'analysis_website',
		});
		setScreen('analysis');
	}, [bridgeApi, screenshotUrl, igThumbnails]);

	const handleAnalysisDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'post_analysis',
			previous_step: 'analysis',
		});
		bridgeApi.sendAction({
			type: 'do_set_onboarding_step',
			phase: 'results',
		});
		setScreen('post_analysis');
	}, [bridgeApi]);

	const handlePostAnalysisDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'strategy_intro',
			previous_step: 'post_analysis',
		});
		setScreen('strategy_intro');
	}, []);

	const handleStrategyIntroDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'background_intro',
			previous_step: 'strategy_intro',
		});
		setScreen('background_intro');
	}, []);

	const handleBackgroundIntroDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'first_job_intro',
			previous_step: 'background_intro',
		});
		setScreen('first_job_intro');
	}, []);

	const handleFirstJobIntroDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'job_preview',
			previous_step: 'first_job_intro',
		});
		setScreen('job_preview');
	}, []);

	const handleJobPreviewDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'job_started',
			previous_step: 'job_preview',
		});
		setScreen('job_started');
	}, []);

	const handleJobStartedDone = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'strategy_pick',
			previous_step: 'job_started',
		});
		setScreen('strategy_pick');
	}, []);

	const handleStrategyPickDone = useCallback((goal: StrategyGoal) => {
		trackEvent('Onboarding Step', {
			step: 'budget',
			previous_step: 'strategy_pick',
			strategy: goal,
		});
		setMonthlyGoal(goal);
		setScreen('budget');
	}, []);

	const handleBudgetDone = useCallback(
		(amount: number) => {
			trackEvent('Onboarding Step', {
				step: 'strategy_summary',
				previous_step: 'budget',
				monthly_budget_usd: amount,
			});
			void saveMarketingBudget(bridgewayApi, userId, amount);
			setMonthlyBudgetUsd(amount);
			setScreen('strategy_summary');
		},
		[bridgewayApi, userId],
	);

	const handleBudgetSkip = useCallback(() => {
		trackEvent('Onboarding Step', {
			step: 'strategy_summary',
			previous_step: 'budget',
			monthly_budget_usd: 0,
			skipped_budget: true,
		});
		// Skip still persists $0 so Bridgeway knows the organic-only posture is
		// deliberate and doesn't re-ask for a budget.
		void saveMarketingBudget(bridgewayApi, userId, 0);
		setMonthlyBudgetUsd(0);
		setScreen('strategy_summary');
	}, [bridgewayApi, userId]);

	const handleStrategySummaryDone = useCallback(
		(pillars: SlotId[]) => {
			completeOnboarding(pillars);
		},
		[completeOnboarding],
	);

	// ---- Render ----

	const renderScreen = () => {
		switch (screen) {
			case 'welcome':
				return <WelcomeScreen onContinue={handleWelcomeDone} />;
			case 'intro':
				return <IntroScreen onContinue={handleIntroDone} />;
			case 'input':
				return (
					<InputScreen
						initialWebsiteUrl={
							bridgeApi.user_state?.onboarding_status?.website_url || ''
						}
						initialInstagramHandle={
							bridgeApi.user_state?.onboarding_status?.instagram_handle || ''
						}
						onSubmit={handleInputSubmit}
					/>
				);
			case 'memory_import_select':
				return (
					<MemoryImportSelectScreen
						websiteUrl={websiteUrl}
						instagramHandle={instagramHandle}
						onPick={handleMemorySelectPick}
						onSkip={handleMemorySelectSkip}
					/>
				);
			case 'memory_import_paste':
				return (
					<MemoryImportPasteScreen
						provider={memoryProvider ?? 'claude'}
						websiteUrl={websiteUrl}
						instagramHandle={instagramHandle}
						initialSnippet={memorySnippet}
						onSubmit={handleMemoryPasteSubmit}
						onSkip={handleMemoryPasteSkip}
					/>
				);
			case 'phone':
				return (
					<PhoneScreen
						initialPhone={phoneNumber}
						onSubmit={handlePhoneSubmit}
					/>
				);
			case 'reply_prompt':
				return (
					<ReplyPromptScreen
						onConfirm={handleReplyConfirmed}
						onDidntGetText={handleReplyBackToPhone}
					/>
				);
			case 'launching': {
				// "Ready" means the onboarding graph has finished *attempting* each
				// asset the user gave us -- the graph now emits the screenshot/IG
				// "ready" events even on failure, so a missing asset no longer blocks
				// Continue until the 180s timeout. We still treat present data as
				// resolved to stay backward-compatible with in-flight sessions that
				// predate the resolved flags.
				const websiteReady =
					!websiteUrl || !!screenshotUrl || screenshotResolved === true;
				const igReady =
					!instagramHandle ||
					(Array.isArray(igThumbnails) && igThumbnails.length > 0) ||
					igDataResolved === true;
				return (
					<OnlinePresenceScreen
						onContinue={handleLaunchingDone}
						ready={(websiteReady && igReady) || readyTimeout}
					/>
				);
			}
			case 'analysis':
				return (
					<AnalysisScreen
						screenshotUrl={screenshotUrl ?? undefined}
						igThumbnails={freshIgThumbnails ?? igThumbnails ?? undefined}
						onDone={handleAnalysisDone}
					/>
				);
			case 'post_analysis':
				return <PostAnalysisScreen onContinue={handlePostAnalysisDone} />;
			case 'strategy_intro':
				return <StrategyIntroScreen onContinue={handleStrategyIntroDone} />;
			case 'background_intro':
				return <BackgroundIntroScreen onContinue={handleBackgroundIntroDone} />;
			case 'first_job_intro':
				return <FirstJobIntroScreen onContinue={handleFirstJobIntroDone} />;
			case 'job_preview':
				return <JobPreviewScreen onStart={handleJobPreviewDone} />;
			case 'job_started':
				return <JobStartedScreen onContinue={handleJobStartedDone} />;
			case 'strategy_pick':
				return (
					<StrategyPickScreen
						initialGoal={monthlyGoal}
						onContinue={handleStrategyPickDone}
					/>
				);
			case 'budget':
				return (
					<BudgetScreen
						initialAmount={monthlyBudgetUsd > 0 ? monthlyBudgetUsd : null}
						onContinue={handleBudgetDone}
						onSkip={handleBudgetSkip}
					/>
				);
			case 'strategy_summary':
				return (
					<StrategySummaryScreen
						goal={monthlyGoal}
						monthlyBudgetUsd={monthlyBudgetUsd}
						onContinue={handleStrategySummaryDone}
					/>
				);
		}
	};

	return (
		<div className="fixed inset-0 z-20 bg-white">
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
				style={{ background: '#F5F0EC' }}
			>
				<div
					className="onb-aurora-blob"
					style={
						{
							'--blob-color': '#6CCEF5',
							'--blob-x1': '38vw',
							'--blob-y1': '-42vh',
							'--blob-x2': '8vw',
							'--blob-y2': '-12vh',
							'--blob-s1': '0.9',
							'--blob-s2': '1.25',
							'--blob-duration': '11s',
						} as React.CSSProperties
					}
				/>
				<div
					className="onb-aurora-blob"
					style={
						{
							'--blob-color': '#F5C48A',
							'--blob-x1': '-46vw',
							'--blob-y1': '-22vh',
							'--blob-x2': '-16vw',
							'--blob-y2': '12vh',
							'--blob-s1': '1.15',
							'--blob-s2': '0.88',
							'--blob-duration': '13s',
							'--blob-delay': '-7s',
						} as React.CSSProperties
					}
				/>
				<div
					className="onb-aurora-blob"
					style={
						{
							'--blob-color': '#C8BEF0',
							'--blob-x1': '2vw',
							'--blob-y1': '-20vh',
							'--blob-x2': '-22vw',
							'--blob-y2': '10vh',
							'--blob-s1': '0.95',
							'--blob-s2': '1.3',
							'--blob-duration': '15s',
							'--blob-delay': '-13s',
						} as React.CSSProperties
					}
				/>
				<div
					className="onb-aurora-blob"
					style={
						{
							'--blob-color': '#F5D8A0',
							'--blob-x1': '-40vw',
							'--blob-y1': '38vh',
							'--blob-x2': '-10vw',
							'--blob-y2': '8vh',
							'--blob-s1': '0.85',
							'--blob-s2': '1.2',
							'--blob-duration': '14s',
							'--blob-delay': '-5s',
						} as React.CSSProperties
					}
				/>
				<div
					className="onb-aurora-blob"
					style={
						{
							'--blob-color': '#C4A4D4',
							'--blob-x1': '42vw',
							'--blob-y1': '42vh',
							'--blob-x2': '12vw',
							'--blob-y2': '14vh',
							'--blob-s1': '1.2',
							'--blob-s2': '0.9',
							'--blob-duration': '17s',
							'--blob-delay': '-9s',
						} as React.CSSProperties
					}
				/>
			</div>
			<div className="relative z-10 h-full w-full" key={screen}>
				{BACK_TARGET[screen] && (
					<button
						type="button"
						aria-label="Back"
						title="Back"
						onClick={handleBack}
						className="absolute z-20 flex size-10 items-center justify-center
							rounded-full border border-(--glass-border) bg-(--glass-bg)
							text-gray-600 shadow-sm transition-all hover:text-gray-900
							hover:shadow active:scale-95"
						style={{
							// Keep the control tappable under notches / rounded corners.
							top: 'max(1rem, env(safe-area-inset-top))',
							left: 'max(1rem, env(safe-area-inset-left))',
						}}
					>
						<ChevronLeftIcon className="size-5 shrink-0" />
					</button>
				)}
				{showMemoryImportNotice && (
					<div
						role="status"
						className="absolute top-4 left-1/2 z-20 flex
							max-w-[calc(100vw-6.5rem)] -translate-x-1/2 items-center gap-3
							rounded-lg bg-white/90 px-4 py-2 text-sm text-gray-700 shadow"
					>
						<span>
							Some of the memories you imported couldn&apos;t be saved.
						</span>
						<button
							type="button"
							aria-label="Dismiss"
							onClick={() => setDismissedMemoryImport(memoryImport)}
							className="text-gray-500 hover:text-gray-700"
						>
							✕
						</button>
					</div>
				)}
				{renderScreen()}
			</div>
		</div>
	);
}
